import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { GetUserProfile, UpdateUserProfile } from "../../Services/Profile";

interface UserProfile {
    _id?: string;
    name?: string;
    family_name?: string;
    email?: string;
    phone_number?: string;
    profile_picture?: string;
}

interface ValidationErrors {
    family_name?: string;
    name?: string;
    phone_number?: string;
}

const Profile: React.FunctionComponent<{}> = (props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState<string>("");
    const [colorSnackbar, setColorSnackbar] = useState<any>("success");
    const [profileData, setProfileData] = useState<UserProfile>();
    const [formData, setFormData] = useState<UserProfile>({...profileData});
    const [errors, setErrors] = useState<ValidationErrors>({});

    useEffect(() => {
        GetUserProfile().then((data) => {
            setProfileData(data);
            setFormData(data);
        }).catch((error) => {
            console.error("Error fetching profile:", error);
        });
    }, []);

    const handleInputChange = (field: keyof UserProfile, value: string) => {
        // If field is phone_number, only allow numbers and +
        if (field === "phone_number") {
            const sanitizedValue = value.replace(/[^0-9+]/g, '');
            setFormData(prev => ({
                ...prev,
                [field]: sanitizedValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
        
        // Clear error for this field when user starts typing
        if (errors[field as keyof ValidationErrors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const validatePhone = (phone: string): boolean => {
        // Check if it contains only digits and optional + at start
        const phoneRegex = /^\+?[0-9]{8,15}$/;
        
        return phoneRegex.test(phone);
    };

    const validateForm = (): boolean => {
        const newErrors: ValidationErrors = {};

        // Validate First Name (family_name)
        if (!formData.family_name || formData.family_name.trim() === "") {
            newErrors.family_name = "First name is required";
        }

        // Validate Last Name (name)
        if (!formData.name || formData.name.trim() === "") {
            newErrors.name = "Last name is required";
        }

        // Validate Phone
        if (!formData.phone_number || formData.phone_number.trim() === "") {
            newErrors.phone_number = "Phone number is required";
        } else if (!validatePhone(formData.phone_number)) {
            newErrors.phone_number = "Please enter a valid phone number (8-15 digits)";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    profile_picture: reader.result as string
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpdateProfile = () => {
        // Validate form before submitting
        if (!validateForm()) {
            setMessage("Please fix the errors before saving");
            setColorSnackbar("error");
            setOpenSnackbar(true);
            return;
        }

        setLoading(true);
        // TODO: Call API to update profile
        UpdateUserProfile(formData).then((data) => {
            setProfileData(formData);
            setIsEditing(false);
            setErrors({});
            setMessage("Profile updated successfully!");
            setColorSnackbar("success");
            setOpenSnackbar(true);
        }).catch((error) => {
            setMessage(error.response?.data?.error || "Failed to update profile.");
            setColorSnackbar("error");
            setOpenSnackbar(true);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleCancel = () => {
        setFormData({...profileData});
        setErrors({});
        setIsEditing(false);
    };

    const handleCloseSnackbar = (
        event?: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason,
    ) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const getInitials = () => {
        const first = formData.family_name?.[0] || "";
        const last = formData.name?.[0] || "";
        return (first + last).toUpperCase();
    };

    return (
        <div className="container-xxl flex-grow-1 container-p-y marg-bottom-100">
            <div className="row mb-4">
                <div className="col-12">
                    <h1 className="title-24 color-black mb-2">Profile</h1>
                    <p className="para-16 color-39393B">Manage your personal information</p>
                </div>
            </div>

            <div className="row justify-content-center">
                <div className="col-lg-12">
                    <div className="card border-0" style={{ borderRadius: "16px", boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.08)" }}>
                        <div className="card-body p-4 p-md-5">
                            {/* Avatar Section */}
                            <div className="d-flex flex-column align-items-center mb-5">
                                <div className="position-relative mb-3">
                                    {formData.profile_picture ? (
                                        <img 
                                            src={formData.profile_picture} 
                                            alt="Profile" 
                                            style={{
                                                width: "120px",
                                                height: "120px",
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                border: "4px solid #746AFF"
                                            }}
                                        />
                                    ) : (
                                        <div 
                                            style={{
                                                width: "120px",
                                                height: "120px",
                                                borderRadius: "50%",
                                                background: "linear-gradient(135deg, #746AFF 0%, #9D95FF 100%)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "40px",
                                                fontWeight: "600",
                                                color: "#FFFFFF",
                                                border: "4px solid #E8E7FF"
                                            }}
                                        >
                                            {getInitials()}
                                        </div>
                                    )}
                                    {isEditing && (
                                        <label 
                                            htmlFor="avatar-upload"
                                            style={{
                                                position: "absolute",
                                                bottom: "0",
                                                right: "0",
                                                width: "36px",
                                                height: "36px",
                                                borderRadius: "50%",
                                                background: "#746AFF",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer",
                                                border: "3px solid #FFFFFF",
                                                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)"
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11.3333 2L14 4.66667L5.33333 13.3333H2.66667V10.6667L11.3333 2Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <input 
                                                type="file" 
                                                id="avatar-upload"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                style={{ display: "none" }}
                                            />
                                        </label>
                                    )}
                                </div>
                                <h3 className="title-20 mb-1">{profileData?.family_name} {profileData?.name}</h3>
                                <p className="para-14 color-475569">{profileData?.email}</p>
                            </div>

                            {/* Form Section */}
                            <div className="row g-4 mb-4">
                                <div className="col-md-6">
                                    <label className="form-label para-14 color-0F172A fw-semibold mb-2">
                                        First Name <span style={{color: "#FF002E"}}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.family_name || ""}
                                        onChange={(e) => handleInputChange("family_name", e.target.value)}
                                        disabled={!isEditing}
                                        style={{
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            border: `1.5px solid ${errors.family_name ? "#FF002E" : "#E2E8F0"}`,
                                            backgroundColor: isEditing ? "#FFFFFF" : "#F8FAFC"
                                        }}
                                    />
                                    {errors.family_name && (
                                        <small style={{color: "#FF002E", fontSize: "12px", marginTop: "4px", display: "block"}}>
                                            {errors.family_name}
                                        </small>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label para-14 color-0F172A fw-semibold mb-2">
                                        Last Name <span style={{color: "#FF002E"}}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.name || ""}
                                        onChange={(e) => handleInputChange("name", e.target.value)}
                                        disabled={!isEditing}
                                        style={{
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            border: `1.5px solid ${errors.name ? "#FF002E" : "#E2E8F0"}`,
                                            backgroundColor: isEditing ? "#FFFFFF" : "#F8FAFC"
                                        }}
                                    />
                                    {errors.name && (
                                        <small style={{color: "#FF002E", fontSize: "12px", marginTop: "4px", display: "block"}}>
                                            {errors.name}
                                        </small>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label para-14 color-0F172A fw-semibold mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={formData.email || ""}
                                        onChange={(e) => handleInputChange("email", e.target.value)}
                                        disabled={true}
                                        style={{
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            border: "1.5px solid #E2E8F0",
                                            backgroundColor: "#F8FAFC"
                                        }}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label para-14 color-0F172A fw-semibold mb-2">
                                        Phone <span style={{color: "#FF002E"}}>*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        placeholder="+1 234 567 890"
                                        value={formData.phone_number || ""}
                                        onChange={(e) => handleInputChange("phone_number", e.target.value)}
                                        disabled={!isEditing}
                                        style={{
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            border: `1.5px solid ${errors.phone_number ? "#FF002E" : "#E2E8F0"}`,
                                            backgroundColor: isEditing ? "#FFFFFF" : "#F8FAFC"
                                        }}
                                    />
                                    {errors.phone_number && (
                                        <small style={{color: "#FF002E", fontSize: "12px", marginTop: "4px", display: "block"}}>
                                            {errors.phone_number}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex justify-content-end gap-3 mt-5">
                                {!isEditing ? (
                                    <button
                                        type="button"
                                        className="button-dash back-746AFF color-FFFFFF border-746AFF"
                                        onClick={() => setIsEditing(true)}
                                        style={{
                                            padding: "12px 32px",
                                            borderRadius: "8px",
                                            fontWeight: "500",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px"
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.3333 2L14 4.66667L5.33333 13.3333H2.66667V10.6667L11.3333 2Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        Edit Profile
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="button-dash back-FFFFFF color-475569 border-E2E8F0"
                                            onClick={handleCancel}
                                            disabled={loading}
                                            style={{
                                                padding: "12px 32px",
                                                borderRadius: "8px",
                                                fontWeight: "500"
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="button-dash back-746AFF color-FFFFFF border-746AFF"
                                            onClick={handleUpdateProfile}
                                            disabled={loading}
                                            style={{
                                                padding: "12px 32px",
                                                borderRadius: "8px",
                                                fontWeight: "500",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px"
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm text-light" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M13.3333 8V13.3333H2.66667V8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M11.3333 4.66667L8 1.33334L4.66667 4.66667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M8 1.33334V10" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Snackbar open={openSnackbar} onClose={handleCloseSnackbar} autoHideDuration={4000}>
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={colorSnackbar || "info"}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Profile;
